import moment from 'moment'
import { type PatientPackageApiOutContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'

export type PlanVisitBalance = {
  visitTypeId: string
  visitTypeName: string
  /** Visits the package currently grants — grows when an admin adds visits. */
  allowed: number
  remaining: number
  /** Visits bought at purchase; never changes. Falls back to `allowed` when the API omits it. */
  purchased: number
}

/**
 * The visit-type balance the admin chart speaks for — the number behind the
 * "N of M visits remaining" row and the Edit plan visits modal (QMD-4371).
 *
 * The backend models a package as potentially covering several visit types, but
 * this flow deliberately sticks to a single one: product ships one visit type
 * per package for now, so we take the first the package carries. `null` when the
 * package grants no visits, so callers can fall back to showing nothing.
 */
export function getPlanVisitBalance(
  programPackage: PatientPackageApiOutContract | null | undefined,
): PlanVisitBalance | null {
  const primary = programPackage?.VisitTypes?.[0]
  // Without a VisitTypeId there is nothing the adjust-visits API can act on, so treat the
  // package as having no usable balance — callers then hide the row/modal rather than send
  // a request with a blank id.
  if (!primary?.VisitTypeId) {
    return null
  }

  return {
    visitTypeId: primary.VisitTypeId,
    visitTypeName: primary.VisitTypeName ?? '',
    allowed: primary.AllowedQuantity ?? 0,
    remaining: primary.RemainingQuantity ?? 0,
    purchased: primary.PurchasedQuantity ?? primary.AllowedQuantity ?? 0,
  }
}

/**
 * `Aug 15, 2026` — a plan's purchase / expiry date for the modal summary card.
 *
 * Rendered in UTC, matching {@link getProgramPlanExpiryLabel}: the dates arrive
 * at midnight UTC, so a local-time render would read the day before west of
 * Greenwich. Returns an em dash when there is no usable date.
 */
export function formatPlanDate(iso: string | null | undefined): string {
  if (!iso) {
    return '—'
  }

  const date = moment.utc(iso, moment.ISO_8601)

  return date.isValid() ? date.format('MMM D, YYYY') : '—'
}
